import React from "react";
import { motion } from "framer-motion";
import { Droplets, ShieldCheck, Activity, Building2, Settings2, BarChart3, MapPin } from "lucide-react";
import { Link } from "react-router-dom";

const solutions = [
  {
    icon: ShieldCheck,
    title: "Arsenic Removal Plant in Kolkata",
    desc: "Dedicated arsenic removal plants for residential societies, villas and high-rise buildings across Kolkata, Newtown and Rajarhat. West Bengal groundwater is critically affected by arsenic contamination — our systems bring arsenic levels below BIS and WHO safe limits for drinking water.",
  },
  {
    icon: ShieldCheck,
    title: "Arsenic-Free Water Treatment for West Bengal",
    desc: "Adsorption-based arsenic removal technology using activated alumina and iron-based media. Designed for borewell-dependent properties in arsenic-prone zones of Kolkata, Newtown, Rajarhat and broader West Bengal — protecting residents from long-term health risks.",
  },
  {
    icon: Droplets,
    title: "Iron Removal Plant in Kolkata",
    desc: "Dedicated iron removal plants for residential societies, high-rise buildings and villas across Kolkata, Newtown and Rajarhat. Removes excess iron and manganese from borewell water — eliminating rust stains, metallic taste and damage to plumbing and fixtures.",
  },
  {
    icon: ShieldCheck,
    title: "Borewell Iron Removal Solutions",
    desc: "Borewell water in Kolkata, Newtown and Rajarhat often carries high iron content. Our iron removal systems use oxidation, aeration and filtration to bring iron levels within safe limits — protecting residents and infrastructure alike.",
  },
  {
    icon: Droplets,
    title: "Softener Plant Installation in Kolkata",
    desc: "Water softener plants for residential societies, villas and high-rise buildings across Kolkata, Newtown and Rajarhat. Removes hardness-causing calcium and magnesium ions — preventing scale buildup in pipes, geysers, washing machines and plumbing fixtures.",
  },
  {
    icon: Settings2,
    title: "Hard Water Softening Solutions",
    desc: "Custom-capacity softener plants designed for borewell and municipal hard water conditions prevalent in Kolkata and Newtown. Ion-exchange resin systems with auto-regeneration for continuous soft water supply to every flat and utility point.",
  },
  {
    icon: ShieldCheck,
    title: "UV System Installation in Kolkata",
    desc: "Ultraviolet (UV) water disinfection systems that eliminate bacteria, viruses and pathogens without chemicals. Installed for residential societies, villas and buildings across Kolkata, Newtown and Rajarhat as a standalone or combined RO+UV solution.",
  },
  {
    icon: ShieldCheck,
    title: "RO + UV Combined Purification Systems",
    desc: "Integrated RO and UV systems that first remove dissolved solids and heavy metals via reverse osmosis, then disinfect with UV light — delivering the highest level of water purity for homes and societies in Kolkata and Newtown.",
  },
  {
    icon: Droplets,
    title: "RO Plant Installation in Kolkata",
    desc: "Design, supply and installation of RO (Reverse Osmosis) plants for residential societies, high-rise buildings and gated communities in Kolkata, Newtown and Rajarhat. Removes dissolved solids, bacteria and heavy metals for safe drinking water.",
  },
  {
    icon: ShieldCheck,
    title: "Commercial & Residential RO Plants",
    desc: "Scalable RO plant systems for individual homes, apartment blocks and large housing townships. Custom capacity planning based on daily demand, source water TDS and occupancy — serving greater Kolkata and Newtown Action Area.",
  },
  {
    icon: Droplets,
    title: "Water Filter Installation in Kolkata",
    desc: "Professional water filter supply and installation for homes, apartments and societies across Kolkata, Newtown and Rajarhat. RO filters, UV filters and multi-stage filters sized for your water source and daily usage.",
  },
  {
    icon: ShieldCheck,
    title: "Residential Water Filter Systems",
    desc: "High-performance water filter systems for individual flats, villas and bungalows. Removes bacteria, dissolved solids, chlorine and sediment — delivering clean, safe drinking water at every tap.",
  },
  {
    icon: Building2,
    title: "Apartment Society Water Treatment Plants",
    desc: "Custom-capacity filtration and purification systems for housing societies across Kolkata, Newtown and Rajarhat. Designed for high daily demand with bacteria removal and safe water delivery to every flat.",
  },
  {
    icon: Droplets,
    title: "Luxury Villa & Mansion Purification Systems",
    desc: "Multi-stage RO and UV purification for independent villas and mansions. Premium water quality matched to luxury living standards in Newtown and surrounding areas.",
  },
  {
    icon: ShieldCheck,
    title: "Bacteria & Contaminant Removal Plants",
    desc: "Advanced treatment technology to eliminate harmful bacteria, suspended solids, and chemical contaminants from borewell, municipal or mixed water sources in West Bengal.",
  },
  {
    icon: Activity,
    title: "High-Rise Residential Filtration Plants",
    desc: "Integrated utility-grade water filtration plants for high-rise towers and complexes in Rajarhat, Newtown and greater Kolkata. Designed for consistent quality at scale.",
  },
  {
    icon: Settings2,
    title: "Smart IoT-Enabled Monitoring Plants",
    desc: "Real-time monitoring of TDS, pH, pressure and flow with remote alerts — giving property managers and facility teams full operational visibility over water quality.",
  },
  {
    icon: BarChart3,
    title: "Custom Capacity Water Treatment Design",
    desc: "Each plant is engineered around your specific occupancy, source-water profile and peak demand. No off-the-shelf approach — every system is sized and configured for your property.",
  },
  {
    icon: Building2,
    title: "Township & Gated Community Infrastructure",
    desc: "End-to-end water treatment infrastructure for townships, gated communities and large real estate developments across Kolkata's New Town Action Area I, II and III.",
  },
  {
    icon: Droplets,
    title: "Borewell & Municipal Water Treatment",
    desc: "Source-adaptive treatment whether your supply is borewell, KMC municipal water or a mixed condition — achieving safe, clean and consistent output for residential use.",
  },
  {
    icon: ShieldCheck,
    title: "Retrofit & Upgrade for Existing Buildings",
    desc: "Upgrading older or underperforming water systems in existing societies and buildings without major structural disruption — serving Kolkata North, South, Salt Lake and Rajarhat.",
  },
];

export default function SolutionsPage() {
  return (
    <div className="mx-auto max-w-7xl px-6 py-20">
      <div className="mx-auto max-w-4xl text-center">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-400">
          Solutions
        </p>
        <h1 className="mt-4 text-4xl font-bold md:text-6xl">
          Arsenic Removal, Iron Removal, RO Plant, UV System &amp; Water Filter in Kolkata, Newtown &amp; Rajarhat
        </h1>
        <p className="mt-6 text-lg leading-8 text-slate-300">
          Custom-designed arsenic removal plants, iron removal plants,
          softener plants, RO plants, UV systems and water filters for
          residential and real estate projects across Kolkata, Newtown,
          Rajarhat and West Bengal.
        </p>
      </div>

      {/* Area served badge */}
      <div className="mx-auto mt-8 flex max-w-3xl flex-wrap justify-center gap-2 text-sm">
        {["Newtown", "Rajarhat", "Salt Lake", "Kolkata North", "Kolkata South", "Greater Kolkata"].map((area) => (
          <span
            key={area}
            className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/20 bg-cyan-500/10 px-3 py-1.5 text-cyan-200"
          >
            <MapPin size={13} />
            {area}
          </span>
        ))}
      </div>

      <div className="mt-14 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        {solutions.map((item, index) => {
          const Icon = item.icon;
          return (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.55, delay: index * 0.06 }}
              className="rounded-3xl border border-white/10 bg-white/5 p-7 backdrop-blur-xl transition duration-300 hover:-translate-y-1 hover:border-cyan-400/30"
            >
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl border border-cyan-400/20 bg-cyan-500/10 text-cyan-300">
                <Icon size={22} />
              </div>
              <h2 className="text-xl font-semibold">{item.title}</h2>
              <p className="mt-3 leading-7 text-slate-400">{item.desc}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Project photo showcase */}
      <div className="mt-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
          className="mb-10 text-center"
        >
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-400">
            Our Work
          </p>
          <h2 className="mt-4 text-3xl font-bold md:text-4xl">
            Real Installations Across Kolkata &amp; Newtown
          </h2>
        </motion.div>

        <div className="grid gap-4 md:grid-cols-3">
          {/* Large left image */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="group relative overflow-hidden rounded-3xl md:row-span-2"
          >
            <img
              src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=800&q=80"
              alt="Industrial water treatment plant Kolkata"
              className="h-full min-h-[280px] w-full object-cover transition duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
            <div className="absolute bottom-0 p-5">
              <p className="text-xs font-semibold uppercase tracking-widest text-cyan-400">Filtration Plant</p>
              <p className="mt-1 font-semibold">Industrial Water Treatment Unit</p>
            </div>
          </motion.div>

          {/* Top right */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="group relative overflow-hidden rounded-3xl"
          >
            <img
              src="https://images.unsplash.com/photo-1460317442991-0ec209397118?auto=format&fit=crop&w=800&q=80"
              alt="Residential society water treatment Newtown"
              className="h-56 w-full object-cover transition duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
            <div className="absolute bottom-0 p-5">
              <p className="text-xs font-semibold uppercase tracking-widest text-cyan-400">Residential</p>
              <p className="mt-1 font-semibold">High-Rise Society, Newtown</p>
            </div>
          </motion.div>

          {/* Middle right */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="group relative overflow-hidden rounded-3xl"
          >
            <img
              src="https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?auto=format&fit=crop&w=800&q=80"
              alt="RO plant installation Kolkata"
              className="h-56 w-full object-cover transition duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
            <div className="absolute bottom-0 p-5">
              <p className="text-xs font-semibold uppercase tracking-widest text-cyan-400">RO Plant</p>
              <p className="mt-1 font-semibold">Multi-Media Filter System</p>
            </div>
          </motion.div>

          {/* Bottom middle */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="group relative overflow-hidden rounded-3xl"
          >
            <img
              src="https://images.unsplash.com/photo-1584432810601-6c7f27d2362b?auto=format&fit=crop&w=800&q=80"
              alt="Water piping network residential Rajarhat"
              className="h-56 w-full object-cover transition duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
            <div className="absolute bottom-0 p-5">
              <p className="text-xs font-semibold uppercase tracking-widest text-cyan-400">Infrastructure</p>
              <p className="mt-1 font-semibold">Water Distribution Network</p>
            </div>
          </motion.div>

          {/* Bottom right */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="group relative overflow-hidden rounded-3xl"
          >
            <img
              src="https://images.unsplash.com/photo-1497436072909-60f360e1d4b1?auto=format&fit=crop&w=800&q=80"
              alt="Clean water treatment plant Kolkata"
              className="h-56 w-full object-cover transition duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
            <div className="absolute bottom-0 p-5">
              <p className="text-xs font-semibold uppercase tracking-widest text-cyan-400">Water Quality</p>
              <p className="mt-1 font-semibold">Purified Water Output</p>
            </div>
          </motion.div>
        </div>

        <div className="mt-4 text-center">
          <Link to="/gallery" className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-400 hover:text-cyan-300 transition">
            View Full Gallery →
          </Link>
        </div>
      </div>

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6 }}
        className="mt-16 rounded-[2rem] border border-white/10 bg-gradient-to-r from-cyan-500/10 via-slate-900/70 to-emerald-500/10 p-8 text-center md:p-12"
      >
        <h2 className="text-3xl font-bold md:text-4xl">
          Need an Arsenic Removal Plant, Iron Removal Plant, RO or UV System in Kolkata?
        </h2>
        <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-slate-300">
          Get a free consultation and custom proposal for your property.
          We supply, design and install arsenic removal plants, iron removal
          plants, softener plants, RO plants, UV systems and water filters
          across Kolkata, Newtown, Rajarhat and surrounding areas.
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
          <Link
            to="/contact"
            className="rounded-2xl bg-cyan-500 px-7 py-3.5 font-semibold text-slate-950 transition hover:bg-cyan-400"
          >
            Get Free Proposal
          </Link>
          <a
            href="tel:+918697702088"
            className="rounded-2xl border border-white/15 bg-white/5 px-7 py-3.5 font-semibold transition hover:bg-white/10"
          >
            Call 8697702088
          </a>
        </div>
      </motion.div>
    </div>
  );
}
